import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Server, Eye, ExternalLink } from "lucide-react";
import { z } from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth/use-auth";
import { patSchema } from "@/lib/auth/validation";

export function Landing() {
  const { isAuthenticated, setToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Detect the ?auth=expired signal set by the 401 handler in client.ts.
  const authExpired = searchParams.get("auth") === "expired";

  // Clean the query param immediately after reading it so a page refresh
  // doesn't re-show the banner.
  useEffect(() => {
    if (authExpired) {
      setSearchParams({}, { replace: true });
    }
  }, [authExpired, setSearchParams]);

  const form = useForm<{ pat: string }>({
    resolver: zodResolver(z.object({ pat: patSchema })),
    defaultValues: { pat: "" },
    mode: "onChange",
  });

  useEffect(() => {
    if (isAuthenticated) navigate("/pick", { replace: true });
  }, [isAuthenticated, navigate]);

  function onSubmit(values: { pat: string }) {
    setToken(values.pat);
    navigate("/pick", { replace: true });
  }

  return (
    <div className="container mx-auto px-4 py-16 flex flex-col items-center">
      <div className="max-w-2xl w-full text-center">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          Understand your GitHub Actions usage
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Privacy-first analytics on any repo you have access to. Month-over-month
          runs, success rate, duration, billable minutes by runner OS &mdash;
          everything stays in your browser.
        </p>
      </div>

      <div className="mt-10 w-full max-w-md space-y-4">
        {authExpired ? (
          <Alert variant="destructive">
            <AlertTitle>Token expired or invalid</AlertTitle>
            <AlertDescription>
              Your token was invalid or has expired. Paste a new one below.
            </AlertDescription>
          </Alert>
        ) : null}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="pat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GitHub Personal Access Token</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="github_pat_… or ghp_…"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isValid}
            >
              Analyze repo
            </Button>
          </form>
        </Form>
      </div>

      <div className="mt-12 w-full max-w-md">
        <h2 className="text-base font-semibold mb-4">How to create a PAT</h2>
        <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
          <li>
            Go to{" "}
            <a
              href="https://github.com/settings/tokens?type=beta"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline underline-offset-2 text-foreground hover:text-muted-foreground"
            >
              github.com/settings/tokens?type=beta
              <ExternalLink className="size-3" />
            </a>{" "}
            and click <strong>Generate new token</strong>.
          </li>
          <li>
            Under <strong>Repository access</strong>, choose{" "}
            <strong>Only select repositories</strong> and tick the repos you want
            to audit.
          </li>
          <li>
            Under <strong>Repository permissions</strong>, set{" "}
            <strong>Actions</strong> to <strong>Read-only</strong> and{" "}
            <strong>Metadata</strong> to <strong>Read-only</strong>. No other
            permissions needed.
          </li>
        </ol>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-12 max-w-4xl w-full">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="size-4" />
              Token sessionStorage only
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Your PAT lives in your tab&apos;s sessionStorage. It is wiped when
            the tab closes. No cookies, no localStorage.
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Server className="size-4" />
              No custom backend
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Your browser talks directly to api.github.com. The token is never
            transmitted to any custom server.
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="size-4" />
              No database, no logs
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Nothing is stored beyond your session. Repo data flows directly
            from your browser to api.github.com &mdash; no intermediary.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
